import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { usersService } from '../../../../../../../app/services/usersService'
import { toast } from '../../../../../../components/ui/use-toast'
import { UserAdminPasswordUpdateParams } from '../../../../../../../app/services/usersService/userAdminPasswordUpdate'
import { useAuth } from '../../../../../../../app/hooks/useAuth'
import { authService } from '../../../../../../../app/services/authService'

const schema = z
  .object({
    oldPassword: z.string(),
    password: z.string(),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'A confirmação da senha tem que ser igual a senha',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof schema>

export const usePasswordAdminChangeController = () => {
  const { id } = useParams()
  const { signin, user } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: UserAdminPasswordUpdateParams) => {
      return usersService.userAdminPasswordUpdate(data)
    },
  })

  const handleResetPassword = handleSubmit(async (data) => {
    try {
      if (id) {
        const response = await mutateAsync({
          oldPassword: data.oldPassword,
          password: data.password,
        })

        if (response.success) {
          if (!user?.email) {
            throw new Error('Não foi possível identificar o e-mail do usuário.')
          }

          const { accessToken } = await authService.signin({
            email: user.email,
            password: data.password,
          })

          signin(accessToken)

          toast({
            title: 'Senha atualizada com sucesso!',
          })
        }
      }
    } catch {
      toast({
        variant: 'destructive',
        title:
          'Senha alterada, mas falhou ao atualizar sessão. Faça login novamente.',
      })
    }
  })

  return {
    register,
    errors,
    isPending,
    handleResetPassword,
  }
}
